// Sitzplan-Service: Zuordnung von Gruppen und Teilnehmern zu Tischen mit PDF-Export
// (Anlage v1.4 Abschnitt 2.7 Punkt 8)
import { API_BASE_URL } from '../config/api.config';

export interface SeatingAssignment {
  id: number;
  bookingId: number;
  bookingNumber: string;
  customerName: string;
  participants: number;
  note?: string | null;
}

export interface SeatingTableData {
  id: number;
  name: string;
  capacity: number;
  sortOrder: number;
  occupied: number;
  assignments: SeatingAssignment[];
}

export interface SeatingBooking {
  id: number;
  bookingNumber: string;
  customerName: string;
  personCount: number;
  assignedCount: number;
  remainingCount: number;
}

export interface SeatingPlan {
  scheduleId: number;
  date: string;
  time: string;
  tables: SeatingTableData[];
  bookings: SeatingBooking[];
}

export interface SeatingTableRequest {
  name: string;
  capacity: number;
  sortOrder: number;
}

export interface SeatingAssignmentRequest {
  bookingId: number;
  participants: number;
  note?: string | null;
}

const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : '',
  };
};

// Fehlermeldung aus der API-Antwort extrahieren (deutsche Meldungen vom Backend)
const throwApiError = async (response: Response): Promise<never> => {
  let message = `Fehler (HTTP ${response.status})`;
  try {
    const data = await response.json();
    message = data.message || data.error || message;
  } catch {
    // Antwort war kein JSON, Standardmeldung verwenden
  }
  throw new Error(message);
};

export const seatingService = {
  /** Sitzplan eines Termins laden (Tische, Zuordnungen, Buchungen mit Restpersonen) */
  async getSeatingPlan(scheduleId: number): Promise<SeatingPlan> {
    const response = await fetch(`${API_BASE_URL}/seating/schedule/${scheduleId}`, {
      headers: authHeaders(),
    });
    if (!response.ok) return throwApiError(response);
    return response.json();
  },

  /** Tisch anlegen */
  async createTable(scheduleId: number, data: SeatingTableRequest): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/seating/schedule/${scheduleId}/tables`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) return throwApiError(response);
  },

  /** Tisch ändern (Name, Kapazität, Sortierung) */
  async updateTable(tableId: number, data: SeatingTableRequest): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/seating/tables/${tableId}`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) return throwApiError(response);
  },

  /** Tisch löschen (samt Zuordnungen) */
  async deleteTable(tableId: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/seating/tables/${tableId}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (!response.ok) return throwApiError(response);
  },

  /** Zuordnung anlegen (Gruppe zu Tisch) */
  async createAssignment(tableId: number, data: SeatingAssignmentRequest): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/seating/tables/${tableId}/assignments`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) return throwApiError(response);
  },

  /** Zuordnung entfernen */
  async deleteAssignment(assignmentId: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/seating/assignments/${assignmentId}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (!response.ok) return throwApiError(response);
  },

  /** Sitzplan-PDF mit Authorization-Header als Blob laden und Download öffnen */
  async downloadSeatingPlanPdf(scheduleId: number, dateLabel: string): Promise<void> {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/seating/schedule/${scheduleId}/pdf`, {
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
      },
    });
    if (!response.ok) return throwApiError(response);

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Sitzplan-${dateLabel}.pdf`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  },
};

export default seatingService;
